/**
 * /auto?id=...&stage=manager|payroll&action=approve|reject
 *
 * Entry-point hit when a manager clicks Approve / Reject inside an Outlook actionable card.
 * Trust model: the URL alone is NOT trusted — the user must be signed in (MSAL) and the
 * underlying API still verifies that they're either the named manager OR have Admin role.
 *
 * For Approve, we fire immediately and show the result.
 * For Reject, we ask for a reason inline first, then fire.
 */
import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { gbp, dateShort } from "../lib/format";

type Action = "approve" | "reject";
type Stage = "manager" | "payroll";

export default function AutoActionPage() {
  const [params] = useSearchParams();
  const id = params.get("id") ?? "";
  const stage = (params.get("stage") ?? "manager") as Stage;
  const action = (params.get("action") ?? "") as Action;
  const nav = useNavigate();
  const qc = useQueryClient();

  const { data: claim, isLoading, error } = useQuery({
    queryKey: ["claim", id],
    queryFn: () => api.expenses.get(id),
    enabled: !!id,
  });

  const [reason, setReason] = useState("");
  const [done, setDone] = useState<{ kind: "approve" | "reject"; }| null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [autoFired, setAutoFired] = useState(false);

  const approve = useMutation({
    mutationFn: () => api.approvals.approve(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["claim", id] });
      qc.invalidateQueries({ queryKey: ["approvals"] });
      setDone({ kind: "approve" });
    },
    onError: (e) => setActionError((e as Error).message),
  });

  const reject = useMutation({
    mutationFn: () => api.approvals.reject(id, reason.trim()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["claim", id] });
      qc.invalidateQueries({ queryKey: ["approvals"] });
      setDone({ kind: "reject" });
    },
    onError: (e) => setActionError((e as Error).message),
  });

  // Auto-fire approve immediately. Reject waits for a reason.
  useEffect(() => {
    if (!claim || autoFired || done) return;
    if (action === "approve") {
      setAutoFired(true);
      approve.mutate();
    }
  }, [claim, action, autoFired, done]);

  // Validate URL params
  if (!id || !["approve", "reject"].includes(action)) {
    return (
      <div className="max-w-xl mx-auto py-12 text-center space-y-4">
        <h1 className="text-2xl font-semibold text-liaise-navy">Invalid link</h1>
        <p className="text-slate-600">This link is missing required information. Please open the email again or visit the approvals page directly.</p>
        <Link to="/approvals" className="inline-block text-liaise-navy hover:underline">Go to approvals →</Link>
      </div>
    );
  }

  if (isLoading) return <div className="text-center text-slate-500 py-12">Loading the claim…</div>;
  if (error) return (
    <div className="max-w-xl mx-auto py-12 space-y-4">
      <h1 className="text-2xl font-semibold text-red-700">Couldn't load the claim</h1>
      <p className="text-slate-600 text-sm">{(error as Error).message}</p>
      <Link to="/approvals" className="inline-block text-liaise-navy hover:underline">← Back to approvals</Link>
    </div>
  );
  if (!claim) return null;

  // If the claim has already been actioned, show the current state
  const isStillPending =
    (stage === "manager" && claim.status === "submitted") ||
    (stage === "payroll" && claim.status === "manager_approved");

  if (!isStillPending && !done) {
    return (
      <div className="max-w-xl mx-auto py-12 space-y-4 text-center">
        <h1 className="text-2xl font-semibold text-liaise-navy">{claim.request_number}</h1>
        <p className="text-slate-600">
          This claim is already <span className="font-medium">{claim.status.replace(/_/g, " ")}</span> — no action needed from you.
        </p>
        <Link
          to={`/approvals/${claim.id}?stage=${stage}`}
          className="inline-block bg-liaise-navy text-white font-medium px-5 py-2.5 rounded-lg hover:bg-liaise-navy-700"
        >
          View claim
        </Link>
      </div>
    );
  }

  // Confirmation after action
  if (done) {
    return (
      <div className="max-w-xl mx-auto py-12 space-y-6 text-center">
        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full text-3xl ${done.kind === "approve" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
          {done.kind === "approve" ? "✓" : "✕"}
        </div>
        <h1 className="text-2xl font-semibold text-liaise-navy">
          {done.kind === "approve" ? "Approved" : "Rejected"}
        </h1>
        <p className="text-slate-600">
          <span className="font-medium">{claim.request_number}</span> — {gbp(Number(claim.total_amount))}
          {claim.claimant_display_name && <> — {claim.claimant_display_name}</>}
        </p>
        <div className="flex gap-3 justify-center pt-2">
          <button onClick={() => nav("/approvals")} className="bg-liaise-navy text-white font-medium px-5 py-2.5 rounded-lg hover:bg-liaise-navy-700">
            Back to approvals
          </button>
          <button onClick={() => nav(`/approvals/${claim.id}?stage=${stage}`)} className="text-slate-600 px-5 py-2.5 rounded-lg hover:bg-slate-100">
            View claim
          </button>
        </div>
      </div>
    );
  }

  // Approve in progress
  if (action === "approve") {
    return (
      <div className="max-w-xl mx-auto py-12 space-y-4 text-center">
        <h1 className="text-2xl font-semibold text-liaise-navy">{claim.request_number}</h1>
        <p className="text-slate-600">
          {gbp(Number(claim.total_amount))} from {claim.claimant_display_name} · {claim.cost_centre_name}
        </p>
        <p className="text-slate-500 text-sm">
          {approve.isPending ? "Approving…" : "Preparing…"}
        </p>
        {actionError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
            {actionError}
            <div className="mt-3">
              <button onClick={() => approve.mutate()} className="text-liaise-navy text-sm hover:underline">Try again</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Reject — ask for reason
  return (
    <div className="max-w-xl mx-auto py-12 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-liaise-navy">Reject claim</h1>
        <p className="text-slate-600 mt-1">
          {claim.request_number} · {gbp(Number(claim.total_amount))} from {claim.claimant_display_name}
        </p>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm">
        <div className="text-slate-600">
          <span className="text-slate-500">Cost centre:</span> {claim.cost_centre_name}<br />
          <span className="text-slate-500">Lines:</span> {claim.lines.length}<br />
          <span className="text-slate-500">Submitted:</span> {dateShort(claim.submitted_at)}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          Reason for rejection (will be sent to {claim.claimant_display_name})
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="e.g. Receipt not attached for line 2 — please add and resubmit."
          className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:border-liaise-navy focus:ring-1 focus:ring-liaise-navy outline-none"
        />
      </div>

      {actionError && <div className="text-red-600 text-sm">{actionError}</div>}

      <div className="flex gap-3">
        <button
          onClick={() => reject.mutate()}
          disabled={!reason.trim() || reject.isPending}
          className="bg-red-600 text-white font-medium px-5 py-2.5 rounded-lg hover:bg-red-700 disabled:opacity-50"
        >
          {reject.isPending ? "Rejecting…" : "Confirm reject"}
        </button>
        <Link to={`/approvals/${claim.id}?stage=${stage}`} className="text-slate-600 px-5 py-2.5 hover:underline">
          View full claim instead
        </Link>
      </div>
    </div>
  );
}
